/**
 * Authentication Repository
 *
 * Life-critical authentication with FERP repository pattern compliance:
 * - Safety framework integration for all auth operations
 * - Immutable audit trail for authentication events
 * - Tenant isolation enforcement
 * - Multi-domain session management (Healthcare: 1h, Business: 2h, Admin: 4h)
 * - Repository pattern compliance (NO direct API calls)
 */

import { BaseRepository } from './BaseRepository';
import { parameterRepository } from './ParameterRepository';

export interface OidcConfig {
    issuer: string;
    clientId: string;
    redirectUri: string;
    scope?: string;
    audience?: string;
    useRefreshToken?: boolean;
    clientSecret?: string;
}

export interface AuthenticationContext {
    operation: 'TOKEN_EXCHANGE' | 'REFRESH_TOKEN' | 'LOGOUT' | 'WELL_KNOWN_FETCH';
    tenantId: string;
    criticalityLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
    domain?: string;  // Domain-agnostic per FERP architecture - resolved via DomainCompatibilityLayer
    authProvider?: string;
}

export interface TokenResponse {
    access_token: string;
    refresh_token?: string;
    id_token?: string;
    token_type: 'Bearer';
    expires_in: number;
    scope?: string;
}

export interface AuthenticationAuditEvent {
    operation: string;
    timestamp: number;
    tenantId: string;
    userId?: string;
    domain?: string;
    authProvider?: string;
    sessionDuration?: number;
    safetyScore?: number;
    success: boolean;
    errorCode?: string;
    ipAddress?: string;
    userAgent?: string;
}

export class AuthenticationRepository extends BaseRepository<any> {
    private sessionManager: any;

    constructor() {
        super();
        this.initAuthSessionManager();
    }

    private initAuthSessionManager(): void {
        // Session manager is deprecated - all durations MUST come from backend
        // Use getSessionDurationForDomain() which fetches from backend
        this.sessionManager = {
            getSessionDuration: (_domain: string) => {
                console.error('DEPRECATED: Use getSessionDurationForDomain() which fetches from backend');
                return 3600; // 1 hour safety default - NEVER use this method
            }
        };
    }

    /**
     * Find authentication record by tenant and ID (FERP Repository Pattern Compliance)
     */
    async findByTenantAndId(tenantId: string, id: string): Promise<any | null> {
        // Log access for audit trail
        await this.auditTrail.logAccess('AUTH_ACCESS', {
            tenantId,
            id,
            operation: 'findByTenantAndId',
            timestamp: new Date().toISOString()
        });

        const query = `
            query FindAuthByTenantAndId($tenantId: String!, $id: ID!) {
                authenticationRecord(tenant: $tenantId, id: $id) {
                    id
                    userId
                    provider
                    tenantId
                    sessionDuration
                    lastAccess
                    expiresAt
                }
            }
        `;

        const result = await this.executeQuery(query, { tenantId, id }, {
            cache: true,
            cacheTime: 60000 // 1 minute for auth records
        });
        return result.authenticationRecord || null;
    }

    /**
     * @deprecated Use getSessionDurationForDomain() which fetches from backend
     * This method will be removed - it exposes hardcoded values
     */
    public getSessionDuration(_domain: string): number {
        console.error('DEPRECATED: getSessionDuration() uses hardcoded values. Use getSessionDurationForDomain() instead');
        throw new Error('Session durations must come from backend configuration via getSessionDurationForDomain()');
    }

    /**
     * Fetch OIDC well-known configuration with safety validation
     */
    async fetchWellKnownConfiguration(
        issuer: string,
        tenantId: string,
        domain?: string
    ): Promise<any> {
        const safetyContext: AuthenticationContext = {
            operation: 'WELL_KNOWN_FETCH',
            tenantId,
            criticalityLevel: 'HIGH',
            domain: domain as any,
            authProvider: issuer
        };

        // MANDATORY: Safety validation for external auth provider access
        const safetyScore = await this.validateAuthSafety(safetyContext);

        if (safetyScore.overall < 70) {
            await this.auditAuthEvent({
                operation: 'WELL_KNOWN_FETCH_BLOCKED',
                timestamp: Date.now(),
                tenantId,
                authProvider: issuer,
                safetyScore: safetyScore.overall,
                success: false,
                errorCode: 'SAFETY_THRESHOLD_NOT_MET'
            });

            throw new Error(`SAFETY BLOCKED: Well-known config fetch blocked (score: ${safetyScore.overall})`);
        }

        // Repository pattern: Fetch through GraphQL instead of direct HTTP
        return this.query(
            `
            query FetchOIDCWellKnown(
                $issuer: String!
                $tenantId: String!
                $domain: String
            ) {
                oidcWellKnown(issuer: $issuer, tenantId: $tenantId, domain: $domain) {
                    issuer
                    authorization_endpoint
                    token_endpoint
                    userinfo_endpoint
                    jwks_uri
                    scopes_supported
                    response_types_supported
                    grant_types_supported
                    subject_types_supported
                    id_token_signing_alg_values_supported
                    claims_supported
                    auditId
                    retrievedAt
                }
            }
            `,
            { issuer, tenantId, domain }
        );
    }

    /**
     * Exchange authorization code for tokens with safety validation
     */
    async exchangeAuthorizationCode(
        config: OidcConfig,
        authCode: string,
        codeVerifier: string,
        tenantId: string,
        domain?: string
    ): Promise<TokenResponse> {
        const safetyContext: AuthenticationContext = {
            operation: 'TOKEN_EXCHANGE',
            tenantId,
            criticalityLevel: 'CRITICAL',
            domain: domain as any,
            authProvider: config.issuer
        };

        // MANDATORY: Safety validation for token exchange
        const safetyScore = await this.validateAuthSafety(safetyContext);

        if (safetyScore.overall < 75) { // Higher threshold for token exchange
            await this.auditAuthEvent({
                operation: 'TOKEN_EXCHANGE_BLOCKED',
                timestamp: Date.now(),
                tenantId,
                authProvider: config.issuer,
                domain,
                safetyScore: safetyScore.overall,
                success: false,
                errorCode: 'SAFETY_THRESHOLD_NOT_MET'
            });

            throw new Error(`SAFETY BLOCKED: Token exchange blocked (score: ${safetyScore.overall})`);
        }

        // Determine session duration based on domain
        const sessionDuration = await this.getSessionDurationForDomain(domain, tenantId);

        // Repository pattern: Token exchange through mutation
        const auditContext = {
            operation: 'AUTH_TOKEN_EXCHANGE',
            timestamp: Date.now(),
            tenantId,
            metadata: {
                authProvider: config.issuer,
                domain,
                sessionDuration,
                safetyScore: safetyScore.overall
            },
            safetyScore: safetyScore.overall
        };

        const result = await this.executeWithAuthAudit(auditContext, async () => {
            return this.mutation(
                `
                mutation ExchangeAuthorizationCode(
                    $config: OIDCConfigInput!
                    $authCode: String!
                    $codeVerifier: String!
                    $tenantId: String!
                    $domain: String
                    $sessionDuration: Int!
                ) {
                    exchangeAuthCode(
                        config: $config
                        authCode: $authCode
                        codeVerifier: $codeVerifier
                        tenantId: $tenantId
                        domain: $domain
                        sessionDuration: $sessionDuration
                    ) {
                        tokens {
                            access_token
                            refresh_token
                            id_token
                            token_type
                            expires_in
                            scope
                        }
                        session {
                            sessionId
                            expiresAt
                            domain
                            tenantId
                        }
                        auditId
                        safetyAssessment {
                            score
                            violations
                        }
                    }
                }
                `,
                {
                    config: this.sanitizeOidcConfig(config),
                    authCode,
                    codeVerifier,
                    tenantId,
                    domain,
                    sessionDuration
                }
            );
        });

        // Audit successful authentication
        await this.auditAuthEvent({
            operation: 'TOKEN_EXCHANGE_SUCCESS',
            timestamp: Date.now(),
            tenantId,
            authProvider: config.issuer,
            domain,
            sessionDuration,
            safetyScore: safetyScore.overall,
            success: true
        });

        return result.exchangeAuthCode.tokens;
    }

    /**
     * Refresh access token with safety validation
     */
    async refreshAccessToken(
        refreshToken: string,
        config: OidcConfig,
        tenantId: string,
        domain?: string
    ): Promise<TokenResponse> {
        const safetyContext: AuthenticationContext = {
            operation: 'REFRESH_TOKEN',
            tenantId,
            criticalityLevel: 'HIGH',
            domain: domain as any,
            authProvider: config.issuer
        };

        const safetyScore = await this.validateAuthSafety(safetyContext);

        if (safetyScore.overall < 70) {
            await this.auditAuthEvent({
                operation: 'TOKEN_REFRESH_BLOCKED',
                timestamp: Date.now(),
                tenantId,
                authProvider: config.issuer,
                domain,
                safetyScore: safetyScore.overall,
                success: false,
                errorCode: 'SAFETY_THRESHOLD_NOT_MET'
            });

            throw new Error(`SAFETY BLOCKED: Token refresh blocked (score: ${safetyScore.overall})`);
        }

        // Repository pattern: Token refresh through mutation
        return this.mutation(
            `
            mutation RefreshAccessToken(
                $refreshToken: String!
                $config: OIDCConfigInput!
                $tenantId: String!
                $domain: String
            ) {
                refreshToken(
                    refreshToken: $refreshToken
                    config: $config
                    tenantId: $tenantId
                    domain: $domain
                ) {
                    access_token
                    refresh_token
                    id_token
                    token_type
                    expires_in
                    scope
                    auditId
                }
            }
            `,
            {
                refreshToken,
                config: this.sanitizeOidcConfig(config),
                tenantId,
                domain
            }
        );
    }

    /**
     * Logout with comprehensive audit trail
     */
    async logout(
        tenantId: string,
        sessionId?: string,
        domain?: string
    ): Promise<void> {
        const safetyContext: AuthenticationContext = {
            operation: 'LOGOUT',
            tenantId,
            criticalityLevel: 'MEDIUM',
            domain: domain as any
        };

        await this.validateAuthSafety(safetyContext);

        // Repository pattern: Logout through mutation
        await this.mutation(
            `
            mutation Logout(
                $tenantId: String!
                $sessionId: String
                $domain: String
            ) {
                logout(
                    tenantId: $tenantId
                    sessionId: $sessionId
                    domain: $domain
                ) {
                    success
                    sessionTerminatedAt
                    auditId
                }
            }
            `,
            { tenantId, sessionId, domain }
        );

        // Audit logout
        await this.auditAuthEvent({
            operation: 'LOGOUT_SUCCESS',
            timestamp: Date.now(),
            tenantId,
            domain,
            success: true
        });
    }

    // Private helper methods

    /**
     * Validate authentication operation safety
     */
    private async validateAuthSafety(context: AuthenticationContext): Promise<any> {
        if (!this.safetyProvider) {
            throw new Error('CRITICAL: Safety provider not initialized for authentication');
        }

        return await this.safetyProvider.validateOperation({
            operation: context.operation,
            domain: 'AUTHENTICATION',
            tenantId: context.tenantId,
            metadata: {
                criticalityLevel: context.criticalityLevel,
                authDomain: context.domain,
                authProvider: context.authProvider
            }
        });
    }

    /**
     * Execute authentication operation with audit trail
     */
    private async executeWithAuthAudit<T>(
        auditContext: any,
        operation: () => Promise<T>
    ): Promise<T> {
        await this.auditTrail.logOperationStart(auditContext);

        try {
            const result = await operation();

            await this.auditTrail.logOperationSuccess({
                ...auditContext,
                result: 'SUCCESS',
                completedAt: Date.now()
            });

            return result;
        } catch (error) {
            await this.auditTrail.logOperationFailure({
                ...auditContext,
                error: (error as any)?.message ?? String(error),
                failedAt: Date.now()
            });

            throw error as Error;
        }
    }

    /**
     * Audit authentication events
     */
    private async auditAuthEvent(event: AuthenticationAuditEvent): Promise<void> {
        await this.auditTrail.logAuthenticationEvent({
            ...event,
            ipAddress: this.getClientIP(),
            userAgent: this.getUserAgent()
        });
    }

    /**
     * Get session duration based on domain (DATA-DRIVEN requirement)
     * Fetches configuration from backend via ParameterRepository
     */
    private async getSessionDurationForDomain(domain?: string, tenantId?: string): Promise<number> {
        try {
            // Life-critical safety: always require tenant context
            if (!tenantId) {
                console.warn('No tenant ID provided for session duration lookup - using minimum duration');
                return 60 * 60 * 1000; // 1 hour safety default
            }

            // Fetch session configuration from backend
            const sessionDurations = await parameterRepository.getConfigurationValue(
                'session.durations',
                tenantId
            );

            if (!sessionDurations) {
                console.warn('No session duration configuration found - using default');
                return 60 * 60 * 1000; // 1 hour default
            }

            // Check domain-specific duration first
            if (domain && sessionDurations.byDomain && sessionDurations.byDomain[domain]) {
                console.log(`Using domain-specific session duration for ${domain}`);
                return sessionDurations.byDomain[domain];
            }

            // Fall back to default duration
            if (sessionDurations.default) {
                console.log('Using default session duration');
                return sessionDurations.default;
            }

            // Ultimate fallback - life-critical safety
            console.warn('No valid session duration found - using minimum duration');
            return 60 * 60 * 1000; // 1 hour safety default

        } catch (error) {
            console.error('Failed to fetch session duration from backend:', error);
            // Life-critical safety: default to shortest duration on error
            return 60 * 60 * 1000; // 1 hour default
        }
    }

    /**
     * Sanitize OIDC config for transmission
     */
    private sanitizeOidcConfig(config: OidcConfig): any {
        return {
            issuer: config.issuer,
            clientId: config.clientId,
            redirectUri: config.redirectUri,
            scope: config.scope,
            audience: config.audience,
            useRefreshToken: config.useRefreshToken
            // NOTE: clientSecret intentionally excluded for security
        };
    }

    /**
     * Get client IP address (for audit trail)
     */
    private getClientIP(): string {
        // In browser environment, IP is not directly accessible
        return 'unknown';
    }

    /**
     * Get user agent (for audit trail)
     */
    private getUserAgent(): string {
        return navigator.userAgent || 'unknown';
    }

    // Repository pattern standard methods

    /**
     * Find authentication session by tenant and session ID
     */
    async findSessionByTenantAndId(tenantId: string, sessionId: string): Promise<any> {
        const safetyContext: AuthenticationContext = {
            operation: 'TOKEN_EXCHANGE', // Reuse existing operation for session lookup
            tenantId,
            criticalityLevel: 'MEDIUM'
        };

        await this.validateAuthSafety(safetyContext);

        return this.query(
            `
            query GetAuthSession($tenantId: String!, $sessionId: String!) {
                authSession(tenantId: $tenantId, sessionId: $sessionId) {
                    sessionId
                    tenantId
                    userId
                    domain
                    createdAt
                    expiresAt
                    lastActivity
                    status
                    authProvider
                }
            }
            `,
            { tenantId, sessionId }
        );
    }

    /**
     * Find active sessions by tenant
     */
    async findActiveSessionsByTenant(tenantId: string): Promise<any[]> {
        const safetyContext: AuthenticationContext = {
            operation: 'TOKEN_EXCHANGE',
            tenantId,
            criticalityLevel: 'MEDIUM'
        };

        await this.validateAuthSafety(safetyContext);

        return this.query(
            `
            query GetActiveAuthSessions($tenantId: String!) {
                activeAuthSessions(tenantId: $tenantId) {
                    sessionId
                    userId
                    domain
                    createdAt
                    expiresAt
                    lastActivity
                    authProvider
                }
            }
            `,
            { tenantId }
        );
    }
}

// Singleton instance for authentication
export const authenticationRepository = new AuthenticationRepository();